/**
 * PassphraseCoach - Minimal passphrase creation experience
 *
 * SECURITY: Never send raw passphrase or canonical string to server.
 * SECURITY: This passphrase will only be used for client-side Argon2/AES-GCM encryption.
 * The canonical string strips FILLER tokens for key derivation.
 *
 * "Simplicity is the ultimate sophistication." — Leonardo da Vinci
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight, ChevronDown } from 'lucide-react'
import { estimatePassphraseEntropy } from '../../lib/passphraseEntropy'
import { suggestImprovements } from '../../lib/passphraseSuggestions'
import { canonicalizeForKey } from '../../lib/passphraseCanonical'
import { getContent, PassphraseLocale } from '../../lib/passphraseContent'
import PassphraseExamples from './PassphraseExamples'
import PassphraseEducationModal from './PassphraseEducationModal'

interface PassphraseCoachProps {
  lidNumber: string;
  onPassphraseReady: (passphrase: string, canonical: string) => void;
  locale?: PassphraseLocale;
}

// Monochrome token styling
const TOKEN_TYPE_COLORS: Record<string, string> = {
  WORD: 'border border-white/20 text-white/80',
  EMOJI: 'border border-white/30 text-white',
  FILLER: 'border border-white/10 text-white/30 italic',
  DIGIT: 'border border-white/20 text-white/70',
  CHAR: 'border border-white/20 text-white/70',
}

const TOKEN_TYPE_LABELS: Record<string, string> = {
  WORD: 'Word',
  EMOJI: 'Emoji',
  FILLER: 'Filler',
  DIGIT: 'Number',
  CHAR: 'Symbol',
};

export default function PassphraseCoach({
  lidNumber,
  onPassphraseReady,
  locale = 'en',
}: PassphraseCoachProps) {
  const [passphrase, setPassphrase] = useState('');
  const [debouncedPassphrase, setDebouncedPassphrase] = useState('');
  const [showEducationModal, setShowEducationModal] = useState(false);
  const [showExplainScore, setShowExplainScore] = useState(false);
  const [showExamples, setShowExamples] = useState(false);
  const [appliedSuggestion, setAppliedSuggestion] = useState<string | null>(null);

  const content = getContent(locale);

  // Debounce passphrase analysis (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPassphrase(passphrase);
    }, 300);

    return () => clearTimeout(timer);
  }, [passphrase]);

  // Analyze passphrase entropy
  const analysis = useMemo(() => {
    if (!debouncedPassphrase) return null;
    return estimatePassphraseEntropy(debouncedPassphrase, locale);
  }, [debouncedPassphrase, locale]);

  // Generate suggestions
  const suggestions = useMemo(() => {
    if (!debouncedPassphrase) return [];
    const result = suggestImprovements(debouncedPassphrase, locale);
    return result.suggestions;
  }, [debouncedPassphrase, locale]);

  // Calculate canonical form
  const canonical = useMemo(() => {
    if (!passphrase) return '';
    return canonicalizeForKey(passphrase, locale);
  }, [passphrase, locale]);

  // Determine entropy strength - monochrome aesthetic
  const getEntropyStrength = (bits: number) => {
    if (bits < 48) return { label: 'Weak', color: 'bg-white/20', textColor: 'text-white/40' }
    if (bits < 70) return { label: 'OK', color: 'bg-white/40', textColor: 'text-white/60' }
    return { label: 'Strong', color: 'bg-white', textColor: 'text-white' }
  }

  const entropyStrength = analysis ? getEntropyStrength(analysis.bits) : null;

  // Apply suggestion handler
  const handleApplySuggestion = useCallback((suggestion: any) => {
    if (suggestion.apply && typeof suggestion.apply === 'function') {
      const before = passphrase;
      const newPassphrase = suggestion.apply(passphrase);
      setPassphrase(newPassphrase);
      setAppliedSuggestion(`Updated: "${before}" → "${newPassphrase}"`);

      // Clear announcement after 3s
      setTimeout(() => setAppliedSuggestion(null), 3000);
    }
  }, [passphrase]);

  // Handle continue
  const handleContinue = useCallback(() => {
    if (analysis && analysis.bits >= 48) {
      onPassphraseReady(passphrase, canonical);
    }
  }, [passphrase, canonical, analysis, onPassphraseReady]);

  // Animation variants (respects prefers-reduced-motion)
  const chipVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.2,
      },
    }),
  };

  const suggestionVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
  };

  return (
    <div className="w-full space-y-8">
      {/* Header */}
      <div className="space-y-3">
        <h2 className="text-3xl font-extralight tracking-tight text-white">
          Create your passphrase
        </h2>
        <p className="text-white/40 font-light">
          A memorable phrase that protects your identity.
        </p>
      </div>

      {/* ΛiD display */}
      <div className="flex items-center gap-3">
        <span className="text-white/20 text-lg font-extralight">Λ</span>
        <span className="font-mono text-white/50 text-sm tracking-wide">
          {lidNumber}
        </span>
      </div>

      {/* Passphrase input */}
      <div className="space-y-4">
        <textarea
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          placeholder={content.placeholder}
          aria-label="Enter your passphrase"
          aria-describedby="passphrase-help"
          className="w-full min-h-[120px] p-0
                   bg-transparent border-0 border-b border-white/10
                   text-white text-lg font-light placeholder-white/20
                   focus:outline-none focus:border-white/30
                   transition-colors duration-300 resize-none"
          spellCheck={false}
          autoComplete="off"
          autoCapitalize="off"
        />

        <div id="passphrase-help" className="space-y-2">
          <p className="text-xs text-white/30 font-light">{content.focusOnWords}</p>
          <button
            onClick={() => setShowEducationModal(true)}
            className="text-xs text-white/40 hover:text-white/60
                     transition-colors duration-300"
          >
            Learn more
          </button>
        </div>
      </div>

      {/* Live Analysis */}
      {analysis && passphrase.length > 0 && (
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-6"
        >
          {/* Token chips - minimal */}
          <div className="space-y-3">
            <p className="text-xs text-white/30 uppercase tracking-widest">
              Breakdown
            </p>
            <div className="flex flex-wrap gap-2">
              <AnimatePresence mode="popLayout">
                {analysis.tokens.map((token, i) => (
                  <motion.div
                    key={`${token.value}-${i}`}
                    custom={i}
                    variants={chipVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    className={`px-3 py-1.5 text-sm font-light ${TOKEN_TYPE_COLORS[token.type] || 'border border-white/10 text-white/60'}`}
                    title={`${TOKEN_TYPE_LABELS[token.type] || 'Token'}: ${token.value}`}
                  >
                    {token.value}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          {/* Entropy meter - minimal */}
          <div className="space-y-4 p-6 border border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-xs text-white/30 uppercase tracking-widest">
                Strength
              </span>
              <span className={`text-xs font-medium ${entropyStrength?.textColor}`}>
                {entropyStrength?.label}
              </span>
            </div>

            {/* Progress bar */}
            <div className="w-full h-px bg-white/10 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min((analysis.bits / 100) * 100, 100)}%` }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className={`h-full ${entropyStrength?.color}`}
              />
            </div>

            <div className="flex items-center justify-between">
              <span className="text-xs text-white/30 font-light">
                {analysis.bits} bits
              </span>
              <button
                onClick={() => setShowExplainScore(!showExplainScore)}
                className="text-xs text-white/30 hover:text-white/50 transition-colors duration-300"
                aria-expanded={showExplainScore}
              >
                {showExplainScore ? 'Hide details' : 'Details'}
              </button>
            </div>

            {/* Expanded details */}
            <AnimatePresence>
              {showExplainScore && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="pt-4 space-y-2 text-xs text-white/40 border-t border-white/10">
                    <p className="text-white/60">Composition:</p>
                    <ul className="space-y-1">
                      <li>
                        {analysis.tokens.filter(t => t.type === 'WORD').length} words
                        <span className="text-white/50 ml-2">+{analysis.tokens.filter(t => t.type === 'WORD').reduce((sum, t) => sum + (t.bits || 0), 0).toFixed(0)} bits</span>
                      </li>
                      {analysis.tokens.some(t => t.type === 'EMOJI') && (
                        <li>
                          {analysis.tokens.filter(t => t.type === 'EMOJI').length} emoji
                          <span className="text-white/50 ml-2">+{analysis.tokens.filter(t => t.type === 'EMOJI').reduce((sum, t) => sum + (t.bits || 0), 0).toFixed(0)} bits</span>
                        </li>
                      )}
                      {analysis.tokens.some(t => t.type === 'DIGIT') && (
                        <li>
                          {analysis.tokens.filter(t => t.type === 'DIGIT').length} numbers
                          <span className="text-white/50 ml-2">+{analysis.tokens.filter(t => t.type === 'DIGIT').reduce((sum, t) => sum + (t.bits || 0), 0).toFixed(0)} bits</span>
                        </li>
                      )}
                      {analysis.tokens.some(t => t.type === 'FILLER') && (
                        <li className="text-white/30">
                          {analysis.tokens.filter(t => t.type === 'FILLER').length} filler (stripped)
                        </li>
                      )}
                    </ul>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.section>
      )}

      {/* Suggestions Panel - minimal */}
      {suggestions.length > 0 && (
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          <p className="text-xs text-white/30 uppercase tracking-widest">
            Suggestions
          </p>

          <div className="space-y-2">
            <AnimatePresence mode="popLayout">
              {suggestions.map((suggestion, i) => (
                <motion.div
                  key={`${suggestion.kind}-${i}`}
                  variants={suggestionVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="flex items-start justify-between gap-4 p-4 border border-white/10"
                >
                  <p className="text-xs text-white/50 font-light">
                    {suggestion.message}
                    {suggestion.estimatedDeltaBits && (
                      <span className="text-white/70 ml-2">
                        +{suggestion.estimatedDeltaBits.toFixed(0)} bits
                      </span>
                    )}
                  </p>

                  {suggestion.apply && (
                    <button
                      onClick={() => handleApplySuggestion(suggestion)}
                      className="text-xs text-white/40 hover:text-white/70
                               transition-colors duration-300 whitespace-nowrap"
                    >
                      Apply
                    </button>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {appliedSuggestion && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xs text-white/50"
            >
              {appliedSuggestion}
            </motion.p>
          )}
        </motion.section>
      )}

      {/* Examples - collapsible */}
      <div className="space-y-3">
        <button
          onClick={() => setShowExamples(!showExamples)}
          className="w-full flex items-center justify-between py-4
                   border-b border-white/10 hover:border-white/20
                   transition-colors duration-300"
          aria-expanded={showExamples}
        >
          <span className="text-xs text-white/40 uppercase tracking-widest">
            Examples
          </span>
          <motion.div
            animate={{ rotate: showExamples ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="w-4 h-4 text-white/30" />
          </motion.div>
        </button>

        <AnimatePresence>
          {showExamples && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <PassphraseExamples locale={locale} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Continue button */}
      {analysis && analysis.bits >= 48 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4 pt-4"
        >
          {analysis.bits < 60 && (
            <p className="text-xs text-white/30 font-light">
              Consider adding more words for better security
            </p>
          )}

          <button
            onClick={handleContinue}
            className="w-full py-4
                     bg-white text-black text-sm font-medium tracking-wide
                     hover:bg-white/90 active:bg-white/80
                     transition-all duration-200
                     flex items-center justify-center gap-3"
          >
            <span>Continue</span>
            <ArrowRight className="w-4 h-4" />
          </button>

          <p className="text-[10px] text-center text-white/20">
            Remember this passphrase. You'll need it to access your ΛiD.
          </p>
        </motion.div>
      )}

      {/* Education Modal */}
      <PassphraseEducationModal
        isOpen={showEducationModal}
        onClose={() => setShowEducationModal(false)}
        locale={locale}
      />
    </div>
  );
}
